import React, { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  RollbackOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

interface ReturnLine {
  bale_no: string
  weight_kg: string
  condition: string
  notes?: string
}

interface MaterialReturn {
  id: string
  return_no: string
  issue_slip_id: string
  issue_slip?: { issue_slip_no: string }
  return_date: string
  total_bales: number
  total_weight_kg: string
  reason: string
  reason_detail?: string
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  lines: ReturnLine[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusColor: Record<string, string> = {
  PENDING: 'orange',
  ACCEPTED: 'green',
  REJECTED: 'red',
}

const statusIcon: Record<string, React.ReactNode> = {
  PENDING: <WarningOutlined />,
  ACCEPTED: <CheckCircleOutlined />,
  REJECTED: <CloseCircleOutlined />,
}

function fmtKg(v: string | number | null | undefined) {
  if (v === null || v === undefined) return '—'
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 3 })
}

// ── View Drawer ───────────────────────────────────────────────────────────────

function ReturnDetailDrawer({
  returnId,
  open,
  onClose,
  onAccept,
  isAccepting,
}: {
  returnId: string | null
  open: boolean
  onClose: () => void
  onAccept: (id: string) => void
  isAccepting: boolean
}) {
  const { data: ret, isLoading } = useQuery<MaterialReturn>({
    queryKey: ['material-return-detail', returnId],
    queryFn: () => apiClient.get(`/material-returns/${returnId}`).then(r => r.data),
    enabled: !!returnId && open,
  })

  const lineCols = [
    {
      title: 'Bale No',
      dataIndex: 'bale_no',
      key: 'bn',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'wt',
      align: 'right' as const,
      render: (v: string) => Number(v).toFixed(3),
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'cond',
      render: (v: string) => (
        <Tag color={v === 'GOOD' ? 'green' : v === 'DAMAGED' ? 'red' : 'orange'}>{v}</Tag>
      ),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (v?: string) => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : '—',
    },
  ]

  return (
    <Drawer
      title={
        <Space>
          <RollbackOutlined />
          Material Return Detail
          {ret && <Tag color={statusColor[ret.status]}>{ret.status}</Tag>}
        </Space>
      }
      open={open}
      onClose={onClose}
      width={640}
      loading={isLoading}
      extra={
        ret?.status === 'PENDING' ? (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={isAccepting}
            onClick={() => ret && onAccept(ret.id)}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            Accept Return
          </Button>
        ) : null
      }
    >
      {ret && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {ret.status === 'PENDING' && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="This return is awaiting godown keeper acceptance. Accepting will move bales back to IN_STOCK."
            />
          )}
          {ret.status === 'ACCEPTED' && (
            <Alert
              type="success"
              showIcon
              message="Return accepted. Bales have been moved back to IN_STOCK in the godown."
            />
          )}

          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Return No" span={2}>
              <Text strong style={{ fontFamily: 'monospace' }}>{ret.return_no}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Issue Slip">
              {ret.issue_slip?.issue_slip_no ?? ret.issue_slip_id}
            </Descriptions.Item>
            <Descriptions.Item label="Return Date">
              {dayjs(ret.return_date).format('DD-MMM-YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Total Bales">{ret.total_bales}</Descriptions.Item>
            <Descriptions.Item label="Total Weight">{fmtKg(ret.total_weight_kg)} kg</Descriptions.Item>
            <Descriptions.Item label="Reason">
              <Tag color="orange">{ret.reason}</Tag>
            </Descriptions.Item>
            {ret.reason_detail && (
              <Descriptions.Item label="Detail">{ret.reason_detail}</Descriptions.Item>
            )}
          </Descriptions>

          <Card
            size="small"
            title={<>Bale Lines ({ret.lines?.length ?? 0})</>}
          >
            <Table
              dataSource={ret.lines ?? []}
              columns={lineCols}
              rowKey="bale_no"
              size="small"
              pagination={false}
              scroll={{ x: 400 }}
            />
          </Card>
        </Space>
      )}
    </Drawer>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function MaterialReturnPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [viewReturnId, setViewReturnId] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data: returns, isLoading } = useQuery<MaterialReturn[]>({
    queryKey: ['material-returns', plantId],
    queryFn: () =>
      apiClient.get('/material-returns', { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const acceptMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/material-returns/${id}/accept`).then(r => r.data),
    onSuccess: () => {
      message.success('Return accepted — bales moved back to IN_STOCK')
      qc.invalidateQueries({ queryKey: ['material-returns'] })
      qc.invalidateQueries({ queryKey: ['material-return-detail'] })
      qc.invalidateQueries({ queryKey: ['issue-slips'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed to accept return'),
  })

  const list = Array.isArray(returns) ? returns : []
  const pendingCount = list.filter(r => r.status === 'PENDING').length

  const columns = [
    {
      title: 'Return No',
      dataIndex: 'return_no',
      key: 'rn',
      width: 160,
      render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text>,
    },
    {
      title: 'Issue Slip',
      key: 'slip',
      width: 160,
      render: (_: unknown, r: MaterialReturn) => (
        <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {r.issue_slip?.issue_slip_no ?? r.issue_slip_id}
        </Text>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'return_date',
      key: 'date',
      width: 120,
      render: (v: string) => dayjs(v).format('DD-MMM-YYYY'),
    },
    {
      title: 'Bales',
      dataIndex: 'total_bales',
      key: 'bales',
      width: 80,
      align: 'right' as const,
      render: (v: number) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'total_weight_kg',
      key: 'wt',
      width: 120,
      align: 'right' as const,
      render: (v: string) => fmtKg(v),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 130,
      render: (v: string) => <Tag color="orange">{v}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: 'PENDING' | 'ACCEPTED' | 'REJECTED') => (
        <Tag color={statusColor[v]} icon={statusIcon[v]}>{v}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, r: MaterialReturn) => (
        <Space size={4}>
          <Button size="small" onClick={() => setViewReturnId(r.id)}>
            View
          </Button>
          {r.status === 'PENDING' && (
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={acceptMut.isPending && acceptMut.variables === r.id}
              onClick={() => acceptMut.mutate(r.id)}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Accept
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <RollbackOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
        Material Returns
      </Title>
      <Text type="secondary">
        Godown keeper view — accept returned bales to move them back to stock
      </Text>

      {pendingCount > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginTop: 16 }}
          message={
            <Text>
              <Text strong>{pendingCount} return{pendingCount > 1 ? 's' : ''}</Text> pending godown keeper acceptance.
              Accepting will move bales back to <Tag color="green" style={{ fontSize: 11 }}>IN_STOCK</Tag>.
            </Text>
          }
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 200 }}
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
          />
          <Space>
            <Tag color="orange" icon={<WarningOutlined />}>
              {pendingCount} Pending
            </Tag>
            <Tag color="green" icon={<InboxOutlined />}>
              {list.filter(r => r.status === 'ACCEPTED').length} Accepted
            </Tag>
          </Space>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20, showTotal: t => `${t} returns` }}
          scroll={{ x: 900 }}
          rowClassName={r => r.status === 'PENDING' ? 'ant-table-row-warning' : ''}
        />
      </Card>

      <ReturnDetailDrawer
        returnId={viewReturnId}
        open={!!viewReturnId}
        onClose={() => setViewReturnId(null)}
        onAccept={id => {
          acceptMut.mutate(id)
          setViewReturnId(null)
        }}
        isAccepting={acceptMut.isPending}
      />
    </div>
  )
}
